import { FilterMenu, FilterMenuBody } from "@faire/slate/components/filter";
import { Radio } from "@faire/slate/components/radio";
import { useEffect, useState } from "react";

import { InfoIcon } from "../../../../components/icons/SlateIcons";

/**
 * Marks our replacement title row so the override below can find it. Also the
 * hook the override keys on, so a menu without an info button keeps Slate's
 * own header untouched.
 */
const CUSTOM_TITLE_CLASS = "insights-filter-menu-title";

/**
 * Hides Slate's popup header for menus that supply their own.
 *
 * FilterMenuBody takes its title as a plain string — it doubles as the popup's
 * aria-label — so there is nowhere to hang a control beside it, and the header
 * is portaled to the body, out of reach of a wrapper class. A global rule
 * scoped by `:has` is what's left. Disposable prototype debt: it targets
 * Slate's BEM classes and may break when Slate updates.
 *
 * The string title still reaches FilterMenuBase, so the popup keeps its
 * accessible name; only the duplicate visible copy is suppressed. This would
 * also swallow a header Clear button, which this component never asks for.
 */
function FilterMenuTitleOverride() {
  return (
    <style>
      {`.fs-filter-menu:has(.${CUSTOM_TITLE_CLASS}) .fs-filter-menu__header {
          display: none;
        }`}
    </style>
  );
}

type FilterOption<T extends string> = {
  value: T;
  label: string;
  /** Optional shorter label shown on the trigger button (defaults to `label`). */
  triggerLabel?: string;
  disabled?: boolean;
};

type InsightsFilterMenuProps<T extends string> = {
  value: T;
  options: FilterOption<T>[];
  onChange: (value: T) => void;
  menuTitle: string;
  name: string;
  menuSubtitle?: string;
  applyToConfirm?: boolean;
  /** Optional prefix shown before the selected value, e.g. "Date range". */
  labelPrefix?: string;
  /**
   * The value this filter sits at when it isn't filtering anything. Supplying it
   * switches the trigger to the applied treatment — a filled chip with a clear
   * button — whenever the selection is something else. Omit it for filters where
   * every option is a real choice rather than a departure from a default, like a
   * date range, which would otherwise always read as applied.
   */
  clearValue?: T;
  /**
   * Puts an info button next to the menu's title, for a filter whose options
   * are terms the reader may not know yet. The explanation belongs here rather
   * than on the page behind the menu, since the options are the only place the
   * terms are load-bearing.
   *
   * Opening it closes the menu: the dialog is reference material, and leaving a
   * popup open underneath it means two floating layers competing for the click
   * that dismisses either one.
   */
  titleInfo?: {
    /** The button's accessible name — the icon carries no text of its own. */
    label: string;
    onClick: () => void;
  };
};

export function InsightsFilterMenu<T extends string>({
  value,
  options,
  onChange,
  menuTitle,
  name,
  menuSubtitle,
  applyToConfirm = false,
  labelPrefix,
  clearValue,
  titleInfo,
}: InsightsFilterMenuProps<T>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(value);
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.triggerLabel ?? selected?.label ?? value;
  const triggerLabel = labelPrefix
    ? `${labelPrefix}: ${selectedLabel}`
    : selectedLabel;

  useEffect(() => {
    if (menuOpen) {
      setPendingValue(value);
    }
  }, [menuOpen, value]);

  const handleSelect = (selectedValue: T) => {
    if (applyToConfirm) {
      setPendingValue(selectedValue);
      return;
    }
    onChange(selectedValue);
    setMenuOpen(false);
  };

  const handleApply = () => {
    onChange(pendingValue);
  };

  const applied = clearValue != null && value !== clearValue;

  const handleClear =
    clearValue == null
      ? undefined
      : () => {
          setPendingValue(clearValue);
          onChange(clearValue);
          setMenuOpen(false);
        };

  return (
    <FilterMenu
      label={triggerLabel}
      applied={applied}
      open={menuOpen}
      onClick={() => setMenuOpen(!menuOpen)}
      onClear={handleClear}
      onClose={() => setMenuOpen(false)}
    >
      <FilterMenuBody
        title={menuTitle}
        footerActionProps={
          applyToConfirm
            ? {
                label: "Apply",
                onClick: handleApply,
              }
            : undefined
        }
      >
        {titleInfo ? (
          <>
            <FilterMenuTitleOverride />
            {/* Slate puts --slate-spacing-element-default between its header
                and the body; this row sits inside the body, whose own gap is
                --large, so the difference comes back off the bottom. */}
            <div
              className={`${CUSTOM_TITLE_CLASS} flex items-center gap-1.5`}
              style={{
                marginBottom:
                  "calc(var(--slate-spacing-element-default) - var(--slate-spacing-element-large))",
              }}
            >
              <p className="type-fs-paragraph-medium">{menuTitle}</p>
              <button
                type="button"
                aria-label={titleInfo.label}
                className="flex text-[#757575] transition-colors hover:text-[#333]"
                onClick={() => {
                  setMenuOpen(false);
                  titleInfo.onClick();
                }}
              >
                <InfoIcon size={16} />
              </button>
            </div>
          </>
        ) : null}
        {menuSubtitle ? (
          <p className="mb-3 text-sm leading-5 font-medium text-[#333]">
            {menuSubtitle}
          </p>
        ) : null}
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            label={option.label}
            checked={(applyToConfirm ? pendingValue : value) === option.value}
            disabled={option.disabled}
            onChange={() => {
              if (!option.disabled) {
                handleSelect(option.value);
              }
            }}
          />
        ))}
      </FilterMenuBody>
    </FilterMenu>
  );
}
